
import React, { useState, useEffect } from 'react';
import { UserProfile } from '../../../types';
import { Loader2, Trash2, Plus, Folder, CornerDownRight, Save, Sparkles, Image as ImageIcon, X } from 'lucide-react';
import MediaLibraryModal from '../MediaLibraryModal';

interface Category {
    id: number;
    name: string;
    slug: string;
    short_description?: string;
    description?: string;
    image_url?: string;
    parent_id?: number | null;
    article_count?: number;
}

interface MediaItem {
    id: number;
    url: string;
}

interface Props {
  currentUser: UserProfile;
}

const CategoryManager: React.FC<Props> = ({ currentUser }) => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [editingCategory, setEditingCategory] = useState<Category | null>(null);
    const [formState, setFormState] = useState({
        name: '',
        short_description: '',
        description: '',
        image_url: '',
        parent_id: ''
    });

    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState<'short' | 'long' | null>(null);

    useEffect(() => {
        fetchCategories();
    }, []);
    
    useEffect(() => {
        if (editingCategory) {
            setFormState({
                name: editingCategory.name,
                short_description: editingCategory.short_description || '',
                description: editingCategory.description || '',
                image_url: editingCategory.image_url || '',
                parent_id: String(editingCategory.parent_id || '')
            });
            setError(null); // Clear errors when selecting a new category
        } else {
            // Reset form when not editing
            setFormState({ name: '', short_description: '', description: '', image_url: '', parent_id: '' });
        }
    }, [editingCategory]);

    // This useEffect hook keeps the `editingCategory` state in sync with the main `categories` list
    // after it's refreshed from the database.
    useEffect(() => {
        if (editingCategory) {
            const freshCategoryData = categories.find(c => c.id === editingCategory.id);
            // If the category still exists in the list and the data is different, update it.
            if (freshCategoryData && JSON.stringify(freshCategoryData) !== JSON.stringify(editingCategory)) {
                setEditingCategory(freshCategoryData);
            }
        }
    }, [categories, editingCategory]);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/cms/blog-categories');
            if (res.ok) {
                setCategories(await res.json());
            } else {
                setError('Failed to load categories.');
            }
        } catch (_e) {
            setError('An error occurred.');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateDescription = async (type: 'short' | 'long') => {
        if (!formState.name) {
            setError("Category name is required to generate a description.");
            return;
        }
        setIsGenerating(type);
        setError(null);
        try {
            const res = await fetch('/api/cms/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'category_description',
                    categoryName: formState.name,
                    descriptionType: type
                })
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || "Failed to generate description.");
            }
            const { htmlContent } = await res.json();
            if (type === 'short') {
                setFormState(prev => ({ ...prev, short_description: htmlContent }));
            } else {
                setFormState(prev => ({ ...prev, description: htmlContent }));
            }
        } catch (e: any) {
            setError(e.message);
        } finally {
            setIsGenerating(null);
        }
    };

    const handleSelectMedia = (media: MediaItem) => {
        setFormState(prev => ({ ...prev, image_url: media.url }));
        setIsMediaModalOpen(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormState(prev => ({ ...prev, [name]: value }));
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formState.name) return;
        setSubmitting(true);
        setError(null);
    
        const isEditing = !!editingCategory;
        const url = '/api/cms/blog-categories';
        const method = isEditing ? 'PUT' : 'POST';
        const body = JSON.stringify({
            ...(isEditing && { id: editingCategory.id }),
            ...formState,
            parent_id: formState.parent_id || null
        });
    
        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body
            });
            if (res.ok) {
                // If creating a new category, reset the form. If editing, keep it open.
                if (!isEditing) {
                    setEditingCategory(null);
                }
                fetchCategories(); // Refresh list for both create and update.
            } else {
                const err = await res.json();
                setError(err.error || `Failed to ${isEditing ? 'update' : 'add'} category.`);
            }
        } catch (_e) {
            setError('An error occurred.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteCategory = async (id: number) => {
        if (!confirm('Are you sure you want to delete this category? Child categories will become root categories.')) return;

        try {
            const res = await fetch('/api/cms/blog-categories', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ ids: [id] })
            });
            if (res.ok) {
                if (editingCategory?.id === id) {
                    setEditingCategory(null);
                }
                fetchCategories();
            } else {
                alert('Failed to delete category.');
            }
        } catch {
            alert('An error occurred.');
        }
    };

    const renderCategoryTree = (parentId: number | null, level = 0) => {
        const children = categories.filter(c => c.parent_id === parentId);
        if (children.length === 0) return null;

        return (
            <ul className={level > 0 ? "pl-4" : ""}>
                {children.map(cat => {
                    const isSelected = editingCategory?.id === cat.id;
                    return (
                        <li key={cat.id}>
                            <div 
                                onClick={() => setEditingCategory(cat)}
                                className={`group my-1 flex items-center gap-2 p-2 rounded border transition-colors cursor-pointer ${isSelected ? 'bg-mystic-accent/10 border-mystic-accent/50' : 'bg-mystic-bg border-gray-800 hover:bg-mystic-btnHover'}`}
                            >
                                {level > 0 && <CornerDownRight className="w-4 h-4 text-gray-600 shrink-0"/>}
                                <Folder className="w-4 h-4 text-mystic-accent shrink-0" />
                                <span className="font-bold text-white">{cat.name}</span>
                                <span className="text-xs text-gray-500 ml-2">/ {cat.slug} /</span>
                                
                                <span className="ml-auto mr-4 text-[10px] font-mono font-bold text-gray-400 bg-black/30 px-2 py-1 rounded border border-gray-700">
                                    {cat.article_count || 0} art.
                                </span>

                                <button onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }} className="text-gray-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                            {renderCategoryTree(cat.id, level + 1)}
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <>
            <div className="grid grid-cols-12 gap-6">
                <div className="col-span-8">
                    <h3 className="text-lg font-bold mb-4">Existing Categories</h3>
                    {loading ? <Loader2 className="animate-spin" /> : renderCategoryTree(null)}
                </div>
                <div className="col-span-4">
                    <form onSubmit={handleFormSubmit} className="bg-mystic-box border border-gray-800 p-6 rounded-lg space-y-4 sticky top-20">
                        <h3 className="text-lg font-bold mb-2 flex justify-between items-center">
                            {editingCategory ? `Editing: ${editingCategory.name}` : 'Add New Category'}
                            {editingCategory && (
                                <button type="button" onClick={() => setEditingCategory(null)} className="text-xs text-gray-500 hover:text-white font-normal">(Cancel)</button>
                            )}
                        </h3>
                        <input type="text" name="name" value={formState.name} onChange={handleInputChange} placeholder="Category Name" required className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white" />
                        
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="text-xs text-gray-400">Short Description</label>
                                <button type="button" onClick={() => handleGenerateDescription('short')} disabled={isGenerating === 'short'} className="text-xs text-mystic-accent hover:text-white flex items-center gap-1 disabled:opacity-50">
                                    {isGenerating === 'short' ? <Loader2 className="w-3 h-3 animate-spin"/> : <Sparkles className="w-3 h-3"/>}
                                    Generate AI
                                </button>
                            </div>
                            <textarea name="short_description" value={formState.short_description} onChange={handleInputChange} placeholder="For previews" className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white h-20"></textarea>
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="text-xs text-gray-400">Long Description</label>
                                <button type="button" onClick={() => handleGenerateDescription('long')} disabled={isGenerating === 'long'} className="text-xs text-mystic-accent hover:text-white flex items-center gap-1 disabled:opacity-50">
                                    {isGenerating === 'long' ? <Loader2 className="w-3 h-3 animate-spin"/> : <Sparkles className="w-3 h-3"/>}
                                    Generate AI
                                </button>
                            </div>
                            <textarea name="description" value={formState.description} onChange={handleInputChange} placeholder="Full content" className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white h-24"></textarea>
                        </div>

                        <div>
                            <label className="text-xs text-gray-400 mb-1 block">Image</label>
                            <div className="aspect-video bg-mystic-inset rounded border-2 border-dashed border-gray-700 flex items-center justify-center relative group">
                               {formState.image_url ? (
                                   <>
                                       <img src={formState.image_url} alt="Category preview" className="max-w-full max-h-full object-contain" />
                                       <button 
                                           type="button" 
                                           onClick={() => setFormState(prev => ({...prev, image_url: ''}))}
                                           className="absolute top-1 right-1 p-1 bg-black/50 rounded-full text-white hover:bg-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                       >
                                           <X className="w-4 h-4" />
                                       </button>
                                   </>
                               ) : (
                                   <ImageIcon className="w-8 h-8 text-gray-600"/>
                               )}
                            </div>
                            <button type="button" onClick={() => setIsMediaModalOpen(true)} className="w-full mt-2 text-xs bg-gray-700 hover:bg-gray-600 text-white font-bold py-1.5 rounded">
                               Select Image
                            </button>
                        </div>

                        <select name="parent_id" value={formState.parent_id} onChange={handleInputChange} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white">
                            <option value="">No Parent</option>
                            {categories
                                .filter(cat => !editingCategory || cat.id !== editingCategory.id) // Prevent selecting self
                                .map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)
                            }
                        </select>
                        <button type="submit" disabled={submitting} className="w-full bg-mystic-accent hover:bg-mystic-accentHover text-black font-bold py-2 rounded flex items-center justify-center gap-2 disabled:opacity-50">
                            {submitting ? <Loader2 className="animate-spin" /> : (editingCategory ? <Save className="w-4 h-4" /> : <Plus />)}
                            {editingCategory ? 'Update Category' : 'Add Category'}
                        </button>
                        {error && <p className="text-red-500 text-xs">{error}</p>}
                    </form>
                </div>
            </div>

            <MediaLibraryModal
                isOpen={isMediaModalOpen}
                onClose={() => setIsMediaModalOpen(false)}
                onSelectMedia={handleSelectMedia}
                currentUser={currentUser}
                contextName={formState.name || 'blog-category'}
            />
        </>
    );
};

export default CategoryManager;
